import React, { useMemo, useState } from "react";
import { IonContent, IonHeader, IonPage } from "@ionic/react";
import MobileAppBar from "../../components/AppBar/MobileAppBar";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Chip,
  Stack,
  Typography,
  Divider,
  IconButton,
  Drawer,
  Button,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useShipmentManager, defaultDateRange } from "../../context/ShipmentManagerProvider";
import type { Shipment, OrderSnapshot, ItemSnapshot } from "../../types/DB2";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { endOfDay, format, startOfDay } from "date-fns";
import { formatStatusLabel } from "../../utils/status";
import DateRangePickerWrapper from "../../components/Calendar/DateRangePickerStyles";

const ShipmentsMobile: React.FC = () => {
  const { shipments, dateRange, setDateRange } = useShipmentManager();
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const effectiveDateRange = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) return dateRange;
    return {
      startDate: new Date(defaultDateRange.startDate),
      endDate: new Date(defaultDateRange.endDate),
      key: defaultDateRange.key ?? "selection",
    };
  }, [dateRange]);

  const sortedShipments = useMemo(() => {
    const arr = [...(shipments || [])];
    arr.sort((a, b) => {
      const ad = (a as any)?.shipmentDate?.toMillis?.() || 0;
      const bd = (b as any)?.shipmentDate?.toMillis?.() || 0;
      return bd - ad;
    });
    return arr;
  }, [shipments]);

  const filteredShipments = useMemo(() => {
    if (!effectiveDateRange?.startDate || !effectiveDateRange?.endDate) return sortedShipments;
    const start = startOfDay(effectiveDateRange.startDate);
    const end = endOfDay(effectiveDateRange.endDate);
    return sortedShipments.filter((shipment) => {
      const shipmentDate = (shipment as any)?.shipmentDate;
      try {
        const date =
          typeof shipmentDate?.toDate === "function"
            ? shipmentDate.toDate()
            : shipmentDate instanceof Date
            ? shipmentDate
            : shipmentDate
            ? new Date(shipmentDate)
            : null;
        if (!date || Number.isNaN(date.getTime())) return false;
        return date >= start && date <= end;
      } catch {
        return false;
      }
    });
  }, [sortedShipments, effectiveDateRange]);

  const formatDate = (s: Shipment) => {
    try {
      const d = (s as any)?.shipmentDate?.toDate?.();
      return d ? d.toLocaleDateString() : "";
    } catch {
      return "";
    }
  };

  const ordersFor = (s: Shipment): [string, OrderSnapshot][] =>
    Object.entries(s.ordersSnapshot || {});

  const formatRange = () => {
    const { startDate, endDate } = (effectiveDateRange || {}) as any;
    if (!startDate || !endDate) return "Select Date Range";
    try {
      return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
    } catch {
      return "Select Date Range";
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <MobileAppBar
          title="Shipments"
          position="static"
          actions={
            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon sx={{ color: '#fff' }} />}
              onClick={() => setShowDatePicker(true)}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.7)',
                textTransform: 'none',
                lineHeight: 1.1,
                '&:hover': { borderColor: '#fff' },
                maxWidth: '70vw',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatRange()}
            </Button>
          }
        />
      </IonHeader>
      <IonContent>
        <Box sx={{ px: 1, pt: 1 }}>
          <List disablePadding>
            {filteredShipments.map((s) => {
              const isOpen = !!openMap[s.id];
              const orderEntries = ordersFor(s);
              const ordersCount = orderEntries.length;
              return (
                <Box key={s.id} sx={{ mb: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <ListItem
                    disableGutters
                    secondaryAction={
                      <IconButton edge="end" onClick={() => setOpenMap((m) => ({ ...m, [s.id]: !isOpen }))}>
                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LocalShippingIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 5 }}>
                          <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600 }}>
                            Shipment {s.id}
                          </Typography>
                          <Chip size="small" label={formatDate(s)} variant="outlined" />
                          {s.status && (
                            <Chip
                              size="small"
                              label={formatStatusLabel(s.status) || s.status}
                              color="info"
                              variant="outlined"
                            />
                          )}
                          <Chip size="small" label={`${ordersCount} orders`} color="default" variant="outlined" />
                        </Stack>
                      }
                    />
                  </ListItem>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 2, pr: 1, pb: 1 }}>
                      <List disablePadding>
                        {orderEntries.map(([orderId, snap]) => {
                          const snapAny = snap as any;
                          const orderNoFromSnap = snapAny?.orderNumber;
                          const orderNoFromPreserved = snapAny?.preservedOrder?.orderNumber;
                          const prefixFromSnap = snapAny?.prefix ?? snapAny?.preservedOrder?.prefix;
                          const included = Object.values(snap?.includedItemsSnapshot || {}) as ItemSnapshot[];
                          const orderNoFromItem = included.length ? (included[0] as any)?.orderNumber : undefined;
                          const finalOrderNo = orderNoFromSnap || orderNoFromPreserved || orderNoFromItem;
                          const stripPrefixInsensitive = (value: string, p?: string): string => {
                            const v = (value ?? '').toString().trim();
                            const prefix = (p ?? '').toString().trim();
                            if (!v) return '';
                            if (prefix) {
                              const variants = new Set<string>([prefix]);
                              if (!prefix.endsWith('-')) variants.add(prefix + '-');
                              if (prefix.endsWith('-')) variants.add(prefix.slice(0, -1));
                              for (const variant of variants) {
                                if (!variant) continue;
                                const len = variant.length;
                                if (v.slice(0, len).toLowerCase() === variant.toLowerCase()) {
                                  return v.slice(len);
                                }
                              }
                            }
                            return v.replace(/^[A-Za-z]+-/, '');
                          };
                          const finalStripped = finalOrderNo ? stripPrefixInsensitive(finalOrderNo, prefixFromSnap) : undefined;
                          const strippedFromPrefix =
                            prefixFromSnap && typeof orderId === 'string' && orderId.startsWith(prefixFromSnap)
                              ? orderId.slice(prefixFromSnap.length)
                              : undefined;
                          const strippedGeneric = typeof orderId === 'string' ? orderId.replace(/^[A-Za-z]+-/, '') : orderId;
                          const displayOrderNo = finalStripped || strippedFromPrefix || strippedGeneric;
                          try {
                            console.log('[ShipmentsMobile] order label sources', {
                              shipmentId: s.id,
                              orderId,
                              orderNoFromSnap,
                              orderNoFromPreserved,
                              orderNoFromItem,
                              prefixFromSnap,
                              display: displayOrderNo,
                            });
                          } catch {}
                          const excluded = Object.values(snap?.excludedItemsSnapshot || {}) as ItemSnapshot[];
                          const destinations = Array.from(
                            new Set(included.map((it) => it.destination || '').filter(Boolean))
                          );
                          return (
                            <Box key={orderId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 0.75 }}>
                              <ListItemButton disableGutters sx={{ px: 1.25, py: 0.75 }}>
                                <ListItemText
                                  primary={
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                      <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 600 }}>
                                        Order {displayOrderNo}
                                      </Typography>
                                      {prefixFromSnap && displayOrderNo && (
                                        <Chip size="small" label={`${prefixFromSnap}${displayOrderNo}`} color="default" variant="outlined" />
                                      )}
                                      <Chip size="small" label={`Included: ${included.length}`} color="success" variant="outlined" />
                                      <Chip size="small" label={`Excluded: ${excluded.length}`} color="warning" variant="outlined" />
                                    </Stack>
                                  }
                                  secondary={
                                    <Typography variant="caption" color="text.secondary">
                                      Destinations: {destinations.length ? destinations.join(', ') : '—'}
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                            </Box>
                          );
                        })}
                        {ordersCount === 0 && (
                          <Box sx={{ px: 2, py: 1 }}>
                            <Typography variant="body2" color="text.secondary">No orders for this shipment.</Typography>
                          </Box>
                        )}
                      </List>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            {filteredShipments.length === 0 && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="text.secondary">No shipments in the selected range.</Typography>
              </Box>
            )}
          </List>
        </Box>
        <Drawer anchor="bottom" open={showDatePicker} onClose={() => setShowDatePicker(false)}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Select Date Range</Typography>
            <DateRangePickerWrapper>
              <DateRangePicker
                onChange={(item: any) => {
                  const range = item.range1 || item.selection || {};
                  const nextStart = range.startDate
                    ? startOfDay(range.startDate)
                    : effectiveDateRange.startDate;
                  const nextEnd = range.endDate
                    ? endOfDay(range.endDate)
                    : effectiveDateRange.endDate;
                  setDateRange({
                    startDate: nextStart,
                    endDate: nextEnd,
                    key: range.key || "selection",
                  });
                }}
                showPreview={false}
                moveRangeOnFirstSelection={false}
                months={1}
                ranges={[{
                  startDate: effectiveDateRange.startDate,
                  endDate: effectiveDateRange.endDate,
                  key: effectiveDateRange.key ?? "selection",
                }]}
                showDateDisplay={false}
                staticRanges={[]}
                inputRanges={[]}
                direction="horizontal"
              />
            </DateRangePickerWrapper>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button variant="contained" onClick={() => setShowDatePicker(false)}>Done</Button>
            </Box>
          </Box>
        </Drawer>
      </IonContent>
    </IonPage>
  );
};

export default ShipmentsMobile;
